import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaMedal,
  FaTrophy,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "../HomePage/SectionTitle";

import { getAllPalmares } from "./../../../services/palmaresServices";
import { formatDateFR } from "../HomePage/homeUtils";

const PalmaresTable = ({ sectionId }) => {
  const [palmaresList, setPalmaresList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchPalmares = async () => {
      try {
        setLoading(true);

        const palmaresData = await getAllPalmares();

        const filteredPalmares = Array.isArray(palmaresData)
          ? palmaresData
              .filter(
                (palmares) =>
                  String(palmares.section_id) === String(sectionId)
              )
              .sort((a, b) => {
                const dateA = new Date(a.date_palmares || 0).getTime();
                const dateB = new Date(b.date_palmares || 0).getTime();
                return dateB - dateA;
              })
          : [];

        if (mounted) setPalmaresList(filteredPalmares);
      } catch (error) {
        console.error("Erreur lors de la récupération des palmarès:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchPalmares();

    return () => {
      mounted = false;
    };
  }, [sectionId]);

  const latestPalmares = useMemo(() => {
    return palmaresList.slice(0, 10);
  }, [palmaresList]);

  if (loading) return null;
  if (palmaresList.length === 0) return null;

  return (
    <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Résultats"
          title="Palmarès "
          highlight="de l’association"
          subtitle="Retrouvez les derniers résultats, distinctions et réussites de cette association."
        />

        <Reveal>
          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <StatCard
              icon={FaTrophy}
              label="Résultats enregistrés"
              value={palmaresList.length}
            />

            <StatCard
              icon={FaMedal}
              label="Derniers affichés"
              value={latestPalmares.length}
            />
          </div>
        </Reveal>

        <Reveal>
          <div className="hidden overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950 md:block">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                    <th className="px-5 py-4">Date</th>
                    <th className="px-5 py-4">Titre</th>
                    <th className="px-5 py-4">Résultats</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 dark:divide-white/10">
                  {latestPalmares.map((palmares) => (
                    <tr
                      key={palmares.id}
                      className="transition hover:bg-slate-50 dark:hover:bg-white/5"
                    >
                      <td className="px-5 py-4 text-slate-600 dark:text-slate-300">
                        <span className="inline-flex items-center gap-2">
                          <FaCalendarAlt className="text-primary" />
                          {formatDateFR(palmares.date_palmares)}
                        </span>
                      </td>

                      <td className="px-5 py-4 font-bold text-slate-950 dark:text-white">
                        {palmares.titre}
                      </td>

                      <td className="px-5 py-4 text-slate-600 dark:text-slate-300">
                        {palmares.resultats}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        <div className="grid gap-4 md:hidden">
          {latestPalmares.map((palmares, index) => (
            <Reveal key={palmares.id} delay={index * 0.04}>
              <article className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-white/10 dark:bg-slate-950">
                <div className="flex items-start justify-between gap-4">
                  <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                    <FaCalendarAlt />
                    {formatDateFR(palmares.date_palmares)}
                  </span>

                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow">
                    <FaTrophy />
                  </span>
                </div>

                <h3 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
                  {palmares.titre}
                </h3>

                <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {palmares.resultats}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-950">
      <div className="flex items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Icon />
        </span>

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

export default PalmaresTable;